import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TrendingUp, Users, BadgeCheck, Loader2 } from "lucide-react";

const RANGES = [
  { label: "7 dní", days: 7 },
  { label: "30 dní", days: 30 },
  { label: "90 dní", days: 90 },
  { label: "1 rok", days: 365 },
];

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card/60 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Icon className="h-4 w-4 text-primary" />{label}</div>
      <div className="mt-1 font-heading text-3xl font-extrabold">{value}</div>
    </div>
  );
}

function BreakdownTable({ title, rows, total, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card/60 p-5" data-testid={testid}>
      <h4 className="font-heading text-sm font-bold uppercase tracking-wide">{title}</h4>
      {(!rows || !rows.length) && <p className="mt-3 text-sm text-muted-foreground">Zatiaľ žiadne dáta.</p>}
      <div className="mt-3 space-y-2.5">
        {(rows || []).map((r) => {
          const pct = total ? Math.round((r.count / total) * 100) : 0;
          return (
            <div key={r.key} data-testid={`${testid}-row`}>
              <div className="flex items-center justify-between text-sm">
                <span className="truncate pr-3 text-foreground">{r.key}</span>
                <span className="shrink-0 text-muted-foreground">{r.count} · {pct}%</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminCampaignPanel() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((d) => {
    setLoading(true);
    api.get(`/admin/campaign-report?days=${d}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  return (
    <div className="space-y-5" data-testid="admin-campaign-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Odkiaľ prichádzajú registrácie – priradené podľa reklamy / kanála (UTM, gclid, fbclid).</p>
        <div className="flex gap-1 rounded-lg border border-border bg-muted/40 p-1">
          {RANGES.map((r) => (
            <button key={r.days} onClick={() => setDays(r.days)} data-testid={`campaign-range-${r.days}`}
              className={`min-h-[36px] rounded-md px-3 text-sm font-medium transition-colors ${days === r.days ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-muted-foreground" data-testid="campaign-loading"><Loader2 className="h-4 w-4 animate-spin" /> Načítavam…</div>}

      {!loading && data && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat icon={Users} label="Registrácie" value={data.total} testid="campaign-stat-total" />
            <Stat icon={BadgeCheck} label="Z toho skúška/predplatné" value={data.paid_or_trial} testid="campaign-stat-paid" />
            <Stat icon={TrendingUp} label="Obdobie" value={`${data.days} dní`} testid="campaign-stat-days" />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <BreakdownTable title="Podľa zdroja" rows={data.by_source} total={data.total} testid="campaign-by-source" />
            <BreakdownTable title="Podľa kampane" rows={data.by_campaign} total={data.total} testid="campaign-by-campaign" />
            <BreakdownTable title="Podľa kanála (medium)" rows={data.by_medium} total={data.total} testid="campaign-by-medium" />
          </div>

          <div className="rounded-xl border border-border bg-card/60 p-5" data-testid="campaign-recent">
            <h4 className="font-heading text-sm font-bold uppercase tracking-wide">Najnovšie registrácie</h4>
            {(!data.recent || !data.recent.length) && <p className="mt-3 text-sm text-muted-foreground">Zatiaľ žiadne registrácie v tomto období.</p>}
            {data.recent && data.recent.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Dátum</th>
                      <th className="pb-2 pr-4 font-medium">Email</th>
                      <th className="pb-2 pr-4 font-medium">Zdroj</th>
                      <th className="pb-2 font-medium">Kampaň</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((u, i) => (
                      <tr key={u.email + i} className="border-b border-border/50" data-testid="campaign-recent-row">
                        <td className="py-2 pr-4 text-muted-foreground">{(u.created_at || "").slice(0, 10)}</td>
                        <td className="py-2 pr-4">{u.email}</td>
                        <td className="py-2 pr-4">{u.source}</td>
                        <td className="py-2 text-muted-foreground">{u.campaign || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => load(days)} data-testid="campaign-refresh">Obnoviť</Button>
          </div>
        </>
      )}
    </div>
  );
}
